import { Fragment, h } from "preact";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import sortBy from "lodash/sortBy";
import uniq from "lodash/uniq";

import usePreviewClickHandler from "../hooks/usePreviewClickHandler";
import { classNames } from "../helpers";
import { sortWorkExperiences } from "../talentHelpers";
import i18n from "../i18n";
import { addStageFilterIfNeeded, selectCanSeeTalentPool } from "../store";

import AddToProjectButton from "./AddToProjectButton.jsx";
import AppliedIcon from "./AppliedIcon.jsx";
import AvatarAndUserInfo from "./AvatarAndUserInfo.jsx";
import Icon from "./Icon.jsx";
import RedactedInfo from "./RedactedInfo.jsx";
import TalentAction from "./TalentAction.jsx";
import TalentMoreAction from "./TalentMoreAction.jsx";
import TalentState from "./TalentState.jsx";
import Tooltip from "./Tooltip.jsx";

function ExperienceLine({ experience, previewMode, current = false }) {
  const TagName = current ? "strong" : "span";
  const from = experience.from ? Date.parse(experience.from) : null;
  const to = experience.to ? Date.parse(experience.to) : null;

  return (
    <span>
      <TagName>
        {experience.role} at{" "}
        {previewMode ? <RedactedInfo inline small w80 /> : experience.company}
      </TagName>{" "}
      • {from ? format(from, "yyyy") : "?"} -{" "}
      {to ? format(to, "yyyy") : "Current"}
    </span>
  );
}

export default function TalentPoolItem({ previewMode, talent, to }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const technicalSkills = (
    Array.isArray(talent.data.technicalSkills)
      ? talent.data.technicalSkills
      : [talent.data.technicalSkills]
  ).filter((x) => x);
  const otherSkills = (
    Array.isArray(talent.data.otherSkills)
      ? talent.data.otherSkills
      : [talent.data.otherSkills]
  ).filter((x) => x);
  const topSkills = talent.data.topSkills;

  const experiences = sortWorkExperiences(talent.data.workExperiences);

  const currentExp = experiences.find((exp) => !exp.to);
  const nextExp = experiences.find((exp) => exp.to);
  let countExp = experiences.length;
  if (currentExp) {
    countExp--;
  }
  if (nextExp) {
    countExp--;
  }

  function handleClick() {
    navigate(to);
  }
  function handleStateChange(newData) {
    dispatch(addStageFilterIfNeeded(newData.state));
  }

  const { handleClickWhenLoggedIn } = usePreviewClickHandler(
    selectCanSeeTalentPool
  );

  return (
    <div onclick={handleClick} class={classNames("talent-pool-item")}>
      <AvatarAndUserInfo
        previewMode={previewMode}
        user={talent.data}
        namePostfix={
          <>
            <TalentState
              class="talent-short-view__state hide-on-small"
              talent={talent}
            />
            <AppliedIcon user={talent.data} class="hide-on-small" />
          </>
        }
      />
      <div class="talent-pool-item__section talent-pool-item__section--head pt-0">
        <ul class="talent-pool-item__details mt-0">
          {currentExp && (
            <li class="talent-pool-item__details-item">
              <span
                class="talent-pool-item__details-label"
                data-testid="current"
              >
                {i18n.t("component.talent_pool_item.current")}
              </span>
              <span class="talent-pool-item__details-content">
                <ExperienceLine
                  experience={currentExp}
                  current
                  previewMode={previewMode}
                />
              </span>
            </li>
          )}
          {nextExp && (
            <li class="talent-pool-item__details-item mb-8">
              <span class="talent-pool-item__details-label">
                {i18n.t("component.talent_pool_item.past")}
              </span>
              <span class="talent-pool-item__details-content">
                <ExperienceLine
                  experience={nextExp}
                  previewMode={previewMode}
                />
                {countExp > 0 && (
                  <span class="talent-pool-item__details-info">
                    {i18n.t("component.talent_pool_item.positions", {
                      count: countExp,
                    })}
                  </span>
                )}
              </span>
            </li>
          )}

          {!!talent.data.yearsOfWorkExperience && (
            <li class="talent-pool-item__details-item">
              <span class="talent-pool-item__details-label">
                {i18n.t("component.talent_pool_item.experience")}
              </span>
              <span
                class="talent-pool-item__details-content"
                data-testid="experience"
              >
                {i18n.t("component.talent_pool_item.years_text", {
                  count: talent.data.yearsOfWorkExperience,
                })}
              </span>
            </li>
          )}

          {topSkills && topSkills.length > 0 && (
            <li class="talent-pool-item__details-item">
              <span class="talent-pool-item__details-label">
                {i18n.t("component.talent_pool_item.skills")}
              </span>
              <span
                class="talent-pool-item__details-content"
                data-testid="top-skills"
              >
                {sortBy(
                  topSkills.map((x) => x.name),
                  (x) => x.toLowerCase()
                ).join(", ")}
              </span>
            </li>
          )}
          {!topSkills &&
            (technicalSkills.length > 0 || otherSkills.length > 0) && (
              <li class="talent-pool-item__details-item">
                <span class="talent-pool-item__details-label">
                  {i18n.t("component.talent_pool_item.skills")}
                </span>
                <span
                  class="talent-pool-item__details-content"
                  data-testid="top-skills"
                >
                  {uniq(technicalSkills.concat(otherSkills)).sort().join(", ")}
                </span>
              </li>
            )}
        </ul>
        <div class="talent-pool-item__meta">
          <AddToProjectButton
            talent={talent}
            onClick={handleClickWhenLoggedIn(() => true)}
          />

          <span
            class="talent-pool-item__meta-item"
            data-tooltip-id={`talent-profile-views-${talent.id}`}
          >
            {talent.seenCount} <Icon class="ml-4 mr-8">visibility</Icon>
          </span>
          <Tooltip
            className="nioomi-tooltip"
            id={`talent-profile-views-${talent.id}`}
          >
            {i18n.t("component.talent_pool_item.seen_count")}
          </Tooltip>

          <TalentAction
            talent={talent}
            onChange={handleStateChange}
            onClick={handleClickWhenLoggedIn(null)}
          />
          <TalentMoreAction talent={talent} />
        </div>
      </div>
    </div>
  );
}
